import { useState, useCallback, useEffect, useLayoutEffect, useRef, MutableRefObject, RefObject } from 'react';
import { closestDate } from './segments';
import { shiftMonth } from '../../../../utils';
import { useCalendarContext } from '../../../../context';

type MutableBoolRef = MutableRefObject<boolean>;

export const DAILY_VIEW_DATE_RANGE_DAYS = 15;

// How much of a day has to show before it counts as the one at the top.
const MIN_VISIBLE_PX = 24;

export const useFirstVisibleDay = (
  containerRef: RefObject<HTMLElement>,
  days: CalendarMonthString[],
  onChange?: (iso: CalendarMonthString) => void
) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const entriesMap = useRef<Record<string, IntersectionObserverEntry>>({});
  const firstVisibleRef = useRef<CalendarMonthString | null>(null);
  const [firstVisible, setFirstVisible] = useState<CalendarMonthString | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    observerRef.current?.disconnect();
    entriesMap.current = {};

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const key = e.target.getAttribute('data-date');

          if (key) entriesMap.current[key] = e;
        });

        let topIso: CalendarMonthString | null = null;
        let topPos = Infinity;

        Object.values(entriesMap.current).forEach((e) => {
          // The day at the top of the viewport is the first visible one. A day taller than the viewport never shows a large
          // share of itself, so it counts by how much of it shows, not by what share. A sliver of the day above does not:
          // anchoring to it keeps the wrong date in view when the order flips or the list restarts.
          if (!e.isIntersecting) return;

          const el = e.target as HTMLElement;
          const iso = el.getAttribute('data-date') as CalendarMonthString | null;

          if (!iso) return;

          const rect = el.getBoundingClientRect();
          const rootRect = container.getBoundingClientRect();
          const shown = Math.min(rect.bottom, rootRect.bottom) - Math.max(rect.top, rootRect.top);

          if (shown < Math.min(MIN_VISIBLE_PX, rect.height)) return;

          const pos = el.offsetTop;

          if (pos < topPos) {
            topPos = pos;
            topIso = iso;
          }
        });

        if (topIso && topIso !== firstVisibleRef.current) {
          firstVisibleRef.current = topIso;
          setFirstVisible(topIso);
          onChange?.(topIso);
        }
      },
      {
        root: container,
        rootMargin: '0px',
        threshold: [0, 0.15, 0.35, 0.5, 0.75, 1],
      }
    );

    days.forEach((d) => {
      const el = container.querySelector<HTMLElement>(`[data-date="${d}"]`);

      if (el) observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [days, onChange]);

  return { firstVisible };
};

export const useScroll = (
  containerRef: RefObject<HTMLElement>,
  {
    programmaticRef,
    sortOrder,
    onNearPastBoundary,
    onNearFutureBoundary,
    recheckKey,
  }: {
    programmaticRef: MutableBoolRef;
    sortOrder?: 'oldest' | 'newest';
    onNearPastBoundary?: () => void;
    onNearFutureBoundary?: () => void;
    // Changes whenever the content changes. Checking again then keeps loading while the content
    // still does not fill the container, because no scroll event can fire in that case.
    recheckKey?: unknown;
  }
) => {
  const rafTickingRef = useRef(false);

  const EXTEND_THRESHOLD = 800;

  useEffect(() => {
    const el = containerRef.current;

    if (!el) return;

    const checkBoundaries = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const nearTop = scrollTop < EXTEND_THRESHOLD;
      const nearBottom = scrollHeight - (scrollTop + clientHeight) < EXTEND_THRESHOLD;

      if (sortOrder === 'newest') {
        if (nearTop) onNearFutureBoundary?.();
        if (nearBottom) onNearPastBoundary?.();
      } else {
        if (nearTop) onNearPastBoundary?.();
        if (nearBottom) onNearFutureBoundary?.();
      }
    };

    const onScroll = () => {
      if (rafTickingRef.current) return;

      rafTickingRef.current = true;

      requestAnimationFrame(() => {
        rafTickingRef.current = false;

        // A scroll the grid made itself (clicking a day, going to a date) is looked at once it has ended. The
        // scroll event arrives while it is still marked as one, and ignoring it altogether meant that nothing asked
        // for more when such a scroll ended near an edge, until the user scrolled by hand.
        if (programmaticRef.current) {
          requestAnimationFrame(checkBoundaries);

          return;
        }

        checkBoundaries();
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });

    // Run once after initial render, and again whenever the content changes.
    const frame = requestAnimationFrame(checkBoundaries);

    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener('scroll', onScroll);
    };
  }, [containerRef, programmaticRef, sortOrder, onNearPastBoundary, onNearFutureBoundary, recheckKey]);
};

export const useProgrammaticScroll = (containerRef: RefObject<HTMLElement>) => {
  const programmaticRef = useRef(false);

  const scrollToDate = useCallback((iso: CalendarMonthString, align: 'start' | 'center' = 'start') => {
    const container = containerRef.current;

    if (!container) return;

    const row = container.querySelector<HTMLElement>(`[data-date="${iso}"]`);

    if (!row) return;
    programmaticRef.current = true;
    if (align === 'start') {
      container.scrollTop = row.offsetTop;
    } else {
      container.scrollTop = Math.max(0, row.offsetTop - container.clientHeight / 3);
    }
    requestAnimationFrame(() => {
      programmaticRef.current = false;
    });
  }, []);

  return { scrollToDate, programmaticRef };
};

export const useCurrentDateAutoScroll = ({
  firstVisible,
  containerRef,
  days,
  ensureDate,
  programmaticRef,
  scrollToDate,
  todayISO,
  isSettled = false,
}: {
  firstVisible: CalendarMonthString | null;
  containerRef: React.RefObject<HTMLElement>;
  days: CalendarMonthString[];
  // Makes sure the date is part of the loaded range, loading around it when it is not.
  ensureDate: (d: CalendarMonthString) => boolean | void;
  programmaticRef: React.MutableRefObject<boolean>;
  scrollToDate: (d: CalendarMonthString, align?: 'start' | 'center') => void;
  todayISO: CalendarMonthString;
  // Nothing is loading, so a date that still has no row is not going to get one.
  isSettled?: boolean;
}) => {
  const { currentDate, setCurrentDate } = useCalendarContext();
  const latestScrollAttemptRef = useRef(0);
  const prevCurrentDateRef = useRef<CalendarMonthString | null>(null);
  const currentDateRef = useRef(currentDate);
  // Always points to the currently visible day so month nav shifts from what the user sees.
  const firstVisibleRef = useRef(firstVisible ?? currentDate);

  const internalSetRef = useRef(false);
  const lastVisibleMonthRef = useRef(currentDate.slice(0, 7));

  const setCurrentDateRef = useRef(setCurrentDate);
  const isSettledRef = useRef(isSettled);

  useEffect(() => {
    currentDateRef.current = currentDate;
    firstVisibleRef.current = firstVisible ?? currentDate;
    setCurrentDateRef.current = setCurrentDate;
    isSettledRef.current = isSettled;
  }, [currentDate, firstVisible, setCurrentDate, isSettled]);

  // Leaving the daily view for the weekly or monthly one: those follow the current date, which the daily view only
  // updates when the month changes. Hand over the date that was in view so the next view shows the same one.
  useEffect(
    () => () => {
      const viewed = firstVisibleRef.current;

      // Nothing to hand over when the date in view is the current one already, such as when the grid is
      // remounted without anything having been scrolled.
      if (viewed && viewed !== currentDateRef.current) setCurrentDateRef.current(viewed);
    },
    []
  );

  const scheduleScrollTo = useCallback(
    (iso: CalendarMonthString) => {
      const attemptId = ++latestScrollAttemptRef.current;
      let tries = 0;
      // Allow ~2s for the API to respond and render new days.
      const MAX_TRIES = 120;
      // A few frames for the list to start loading after a jump. Once it has settled without a row for the date,
      // there is nothing left to wait for.
      const SETTLED_TRIES = 8;
      const watched = containerRef.current;
      const userInputs = ['wheel', 'touchstart', 'mousedown'] as const;

      const stopWatchingUser = () => userInputs.forEach((type) => watched?.removeEventListener(type, onUserInput));

      // Someone who starts scrolling themselves must not be pulled back to the date when it finally turns up.
      function onUserInput() {
        stopWatchingUser();

        if (attemptId === latestScrollAttemptRef.current) latestScrollAttemptRef.current += 1;
      }

      userInputs.forEach((type) => watched?.addEventListener(type, onUserInput, { passive: true }));

      const scrollToNearestRendered = (container: HTMLElement) => {
        const allRows = Array.from(container.querySelectorAll<HTMLElement>('[data-date]'));

        if (!allRows.length) return;

        // The closest rendered day on either side of the target, whichever way the rows run.
        const nearestIso = closestDate(allRows.map((r) => r.getAttribute('data-date') ?? '').filter(Boolean), iso) as CalendarMonthString | undefined;

        if (!nearestIso) return;

        programmaticRef.current = true;
        scrollToDate(nearestIso, 'start');
        requestAnimationFrame(() => {
          programmaticRef.current = false;
        });
      };

      const step = () => {
        if (attemptId !== latestScrollAttemptRef.current) {
          stopWatchingUser();

          return;
        }

        const elContainer = containerRef.current;

        if (!elContainer) {
          stopWatchingUser();

          return;
        }

        const row = elContainer.querySelector<HTMLElement>(`[data-date="${iso}"]`);

        if (row) {
          stopWatchingUser();
          programmaticRef.current = true;
          scrollToDate(iso, 'start');
          requestAnimationFrame(() => {
            programmaticRef.current = false;
          });

          return;
        }

        const gaveUpWaiting = tries >= SETTLED_TRIES && isSettledRef.current;

        if (!gaveUpWaiting && tries++ < MAX_TRIES) {
          if (!days.includes(iso)) ensureDate(iso);
          requestAnimationFrame(step);
        } else {
          // Target day has no messages so it never renders — jump to nearest rendered day.
          stopWatchingUser();
          scrollToNearestRendered(elContainer);
        }
      };

      requestAnimationFrame(step);
    },
    [containerRef, days, ensureDate, programmaticRef, scrollToDate]
  );

  const scrollAndAnchor = useCallback(
    (iso: CalendarMonthString) => {
      ensureDate(iso);
      scheduleScrollTo(iso);
    },
    [ensureDate, scheduleScrollTo]
  );

  useEffect(() => {
    prevCurrentDateRef.current = currentDate;
    scrollAndAnchor(currentDate);
  }, []);

  useEffect(() => {
    if (!firstVisible) return;

    const visibleMonth = firstVisible.slice(0, 7);

    if (visibleMonth === lastVisibleMonthRef.current) return;

    lastVisibleMonthRef.current = visibleMonth;

    if (firstVisible !== currentDateRef.current) {
      internalSetRef.current = true;
      setCurrentDate(firstVisible);
    }
  }, [firstVisible, setCurrentDate]);

  useEffect(() => {
    if (prevCurrentDateRef.current === currentDate) {
      return;
    }
    prevCurrentDateRef.current = currentDate;

    if (internalSetRef.current) {
      internalSetRef.current = false;

      return;
    }

    scrollAndAnchor(currentDate);
  }, [currentDate, scrollAndAnchor]);

  useEffect(() => {
    const onToday = () => {
      requestAnimationFrame(() => scrollAndAnchor(todayISO));
    };
    // Shift from the currently visible day so clicking prev/next stays on the same day-of-month.
    const onNext = () => {
      requestAnimationFrame(() => scrollAndAnchor(shiftMonth(firstVisibleRef.current, 1)));
    };
    const onPrev = () => {
      requestAnimationFrame(() => scrollAndAnchor(shiftMonth(firstVisibleRef.current, -1)));
    };

    window.addEventListener('calendar:goToToday', onToday);
    window.addEventListener('calendar:goToNextMonth', onNext);
    window.addEventListener('calendar:goToPrevMonth', onPrev);

    return () => {
      window.removeEventListener('calendar:goToToday', onToday);
      window.removeEventListener('calendar:goToNextMonth', onNext);
      window.removeEventListener('calendar:goToPrevMonth', onPrev);
    };
  }, [scrollAndAnchor, todayISO]);
};

// While a new list is loading the grid keeps one date pinned to the top, so the list does not jump as the
// first pages arrive. This holds the state of that: whether the first load has completed, and the date to
// pin. It restarts whenever the key changes (new filters, or showing or hiding empty dates), pinning the
// date that was in view, and the first load of the page pins today.
export const usePinUntilLoaded = (
  restartKey: string,
  firstVisible: CalendarMonthString | null,
  initialPinDate: CalendarMonthString | null = null
) => {
  const initialLoadCompleteRef = useRef(false);
  // The first load pins the date the view opens on, which is the current date.
  const pinDateRef = useRef<CalendarMonthString | null>(initialPinDate);
  const restartKeyRef = useRef(restartKey);
  const firstVisibleRef = useRef(firstVisible);

  useEffect(() => {
    firstVisibleRef.current = firstVisible;
  }, [firstVisible]);

  useEffect(() => {
    // Only a change of key restarts it, not the first run, which would drop the date the view opened on.
    if (restartKeyRef.current === restartKey) return;

    restartKeyRef.current = restartKey;
    initialLoadCompleteRef.current = false;
    pinDateRef.current = firstVisibleRef.current;
  }, [restartKey]);

  // A jump to a date the list did not hold replaces it, which pins that date the same way until the new one has loaded.
  const pinTo = useCallback((date: CalendarMonthString) => {
    initialLoadCompleteRef.current = false;
    pinDateRef.current = date;
  }, []);

  return { initialLoadCompleteRef, pinDateRef, pinTo };
};

// Ends the pin once the list that replaced the old one has its first pages. Call it after the layout effect
// that applies the pin, so the pin is applied once for the commit that brings the new list in.
//
// It has to look again whenever the list is replaced, and not only when `isLoading` changes: a list served from
// the cache is never loading, so `isLoading` does not change and the pin would never be released. Every scroll
// would then be pulled back to the pinned date.
export const useReleasePinWhenLoaded = (
  { initialLoadCompleteRef, pinDateRef }: Pick<ReturnType<typeof usePinUntilLoaded>, 'initialLoadCompleteRef' | 'pinDateRef'>,
  isLoading: boolean,
  segments: CalendarSegment[]
) => {
  useLayoutEffect(() => {
    if (!isLoading) {
      initialLoadCompleteRef.current = true;
      pinDateRef.current = null;
    }
  }, [isLoading, segments, initialLoadCompleteRef, pinDateRef]);
};
